/**
 * @file configurations.tsx
 * @module app/modules/setting/configurations
 * @description Configuration tab for feature-flag settings. Fetches setting
 * metadata and values from backend, groups options by domain, and renders
 * dynamic controls based on the control/type configuration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.9
 * @version 2.1.2
 */

import { Accordion, Button, Card } from 'react-bootstrap';
import Loader from '../../../Global/loader';
import SettingRow from './components/setting-row';
import WarehouseSelector from './components/warehouse-selector';
import SettingControl from './components/setting-control';
import { useConfigurations } from './hooks/use-configurations';
import type { IFlagItem } from './types/configurations.types';
import {
  notificationSoundKey,
  playNotificationSoundChoice,
  previewNotification,
} from '../_common/utils/sweet-alert';
import { CONFIGURATION_SETTING_KEYS } from '../../../utils/configurationSettingKeys';

const NOTIFICATION_RECIPIENTS_SUFFIX = '--RecipientsUserType';

/**
 * Configuration tab component.
 * @returns {JSX.Element} Configuration accordion UI.
 */
const ConfigurationsTab = () => {
  const projectVersion = process.env.REACT_APP_VERSION || process.env.npm_package_version || 'unknown';
  const commitHash = process.env.REACT_APP_COMMIT_HASH || process.env.REACT_APP_GIT_COMMIT || 'unknown';

  const {
    loading,
    values,
    updatingKeys,
    itemWarehouseId,
    warehouseList,
    groups,
    getGroupRecords,
    buildSubgroupTitle,
    buildItemDescription,
    executeConfigurationAction,
    handleItemWarehouseChange,
    persistConfigurationValue,
  } = useConfigurations();

  /**
   * Previews the notification row with the values currently on the form.
   * @param {IFlagItem} item Setting metadata item.
   * @returns {void}
   */
  const previewNotificationRow = (item: IFlagItem) => {
    previewNotification(item.key, {
      defaultType: String(values?.[CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_TYPE] ?? ''),
      basicScript: String(values?.[CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_SWAL_BASIC_TYPE] ?? ''),
      eventScript: String(values?.[item.key] ?? ''),
      defaultSound: String(values?.[CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_SOUND] ?? ''),
      sounds: Object.fromEntries(
        Object.entries(values || {}).map(([key, value]) => [key, String(value ?? '')]),
      ),
    });
  };

  const onSettingChange = (item: IFlagItem, nextValue: any) => {
    persistConfigurationValue(item, nextValue);

    const isSound = item.key === CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_DEFAULT_SOUND
      || item.key.endsWith('--Sound');
    if (!isSound) {
      return;
    }

    playNotificationSoundChoice(String(nextValue ?? ''));
  };

  const isNotificationEvent = (item: IFlagItem) => (
    item.group === 'notification'
    && item.control === 'textarea'
    && Boolean(item.subgroup)
  );

  const renderRowControl = (
    item: IFlagItem,
    soundItem?: IFlagItem,
    recipientItem?: IFlagItem,
  ) => {
    if (isNotificationEvent(item)) {
      return (
        <>
          <div className="col-md-6 setting-notification-recipients">
            <WarehouseSelector
              item={item}
              value={itemWarehouseId[item.key]}
              disabled={Boolean(updatingKeys[item.key])}
              warehouseList={warehouseList}
              onChange={handleItemWarehouseChange}
            />
            {recipientItem && (
              <div className="setting-notification-field">
                <span className="setting-notification-field-label">{recipientItem.title}</span>
                <SettingControl
                  item={recipientItem}
                  value={values?.[recipientItem.key]}
                  updating={Boolean(updatingKeys[recipientItem.key])}
                  onExecuteAction={executeConfigurationAction}
                  onChange={onSettingChange}
                />
              </div>
            )}
          </div>
          <div className="col-md-6 setting-notification-script">
            <div className="setting-notification-field setting-notification-field--script">
              <span className="setting-notification-field-label">Swal setting</span>
              <SettingControl
                item={item}
                value={values?.[item.key]}
                updating={Boolean(updatingKeys[item.key])}
                onExecuteAction={executeConfigurationAction}
                onChange={onSettingChange}
              />
            </div>
            {soundItem && (
              <div className="setting-notification-field">
                <span className="setting-notification-field-label">Notification sound</span>
                <SettingControl
                  item={soundItem}
                  value={values?.[soundItem.key]}
                  updating={Boolean(updatingKeys[soundItem.key])}
                  onExecuteAction={executeConfigurationAction}
                  onChange={onSettingChange}
                />
              </div>
            )}
            <div className="setting-notification-action">
              <Button className="uw-btn uw-btn-light uw-btn-sm"
                type="button"
                size="sm"
                variant="light"
                onClick={() => previewNotificationRow(item)}
              >
                Try me
              </Button>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="col-md-6 setting-control-context">
          <WarehouseSelector
            item={item}
            value={itemWarehouseId[item.key]}
            disabled={Boolean(updatingKeys[item.key])}
            warehouseList={warehouseList}
            onChange={handleItemWarehouseChange}
          />
        </div>
        <div className="col-md-6 setting-control-main d-flex align-items-start justify-content-end">
          <div className="setting-notification-field">
            <SettingControl
              item={item}
              value={values?.[item.key]}
              updating={Boolean(updatingKeys[item.key])}
              onExecuteAction={executeConfigurationAction}
              onChange={onSettingChange}
            />
            {item.group === 'notification' && (
              <Button
                type="button"
                size="sm"
                variant="light"
                className="uw-btn uw-btn-light uw-btn-sm mt-2"
                onClick={() => previewNotificationRow(item)}
              >
                Try me
              </Button>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <Card className="setting-panel-card">
      <Card.Body className="setting-panel-body">
        <p className="text-black fs-15 mb-6">
          Environment: {process.env.NODE_ENV || 'unknown'}
          <br />
          Version: {projectVersion}
          <br />
          Commit: {commitHash}
        </p>

        {loading ? (
          <div className="d-flex justify-content-center py-10">
            <Loader loading={loading} />
          </div>
        ) : (
          <Accordion
            defaultActiveKey={groups[0]?.key || ''}
            alwaysOpen
            className="setting-accordion-wrap"
          >
            {groups.map((group) => {
              const groupRecords = getGroupRecords(group.key);
              const plainRecords = groupRecords.filter((item) => (
                !(item.subgroup || '').trim()
                && item.control !== 'hidden'
                && !item.key.endsWith('--Sound')
              ));
              const subgroupRecordsMap = groupRecords.reduce((acc, item) => {
                const subgroupKey = String(item.subgroup || '').trim();
                const pairedWithRow =
                  item.key.endsWith('--Sound') ||
                  item.key.endsWith(NOTIFICATION_RECIPIENTS_SUFFIX);
                if (!subgroupKey || item.control === 'hidden' || pairedWithRow) {
                  return acc;
                }

                const subgroupCaption = String(item.subgroupCaption || '').trim();

                const normalizedKey = subgroupKey.toLowerCase();
                if (!acc[normalizedKey]) {
                  acc[normalizedKey] = {
                    legend: subgroupCaption || buildSubgroupTitle(subgroupKey),
                    items: [],
                  };
                }

                if (!acc[normalizedKey].legend && subgroupCaption) {
                  acc[normalizedKey].legend = subgroupCaption;
                }

                acc[normalizedKey].items.push(item);
                return acc;
              }, {} as Record<string, { legend: string; items: IFlagItem[] }>);

              const subgroupBlocks = Object.entries(subgroupRecordsMap);

              return (
                <Accordion.Item
                  eventKey={group.key}
                  key={group.key}
                  className="setting-accordion-item"
                >
                  <Accordion.Header className="setting-accordion-header">{group.title}</Accordion.Header>
                  <Accordion.Body className="setting-accordion-body">
                    {groupRecords.length === 0 ? (
                      <p className="text-muted fs-14 mb-0">No options available.</p>
                    ) : (
                      <>
                        {plainRecords.map((item) => (
                          <SettingRow
                            key={item.key}
                            title={item.title}
                            description={buildItemDescription(item)}
                            codeSample={item.defaultFormat === 'code' ? String(item.defaultValue ?? '') : ''}
                            control={renderRowControl(item)}
                          />
                        ))}

                        {subgroupBlocks.map(([subgroupKey, subgroup], index) => (
                          <fieldset
                            key={subgroupKey}
                            className={`setting-subgroup-fieldset ${index === subgroupBlocks.length - 1 ? 'setting-subgroup-fieldset--last' : ''}`}
                          >
                            <legend className="setting-subgroup-legend">{subgroup.legend}</legend>
                            {subgroup.items.map((item) => (
                              <SettingRow
                                key={item.key}
                                title={item.title}
                                description={buildItemDescription(item)}
                                codeSample={item.defaultFormat === 'code' ? String(item.defaultValue ?? '') : ''}
                                control={renderRowControl(
                                  item,
                                  groupRecords.find((soundItem) => soundItem.key === notificationSoundKey(item.key)),
                                  groupRecords.find((recipientItem) => recipientItem.key === `${item.key}${NOTIFICATION_RECIPIENTS_SUFFIX}`),
                                )}
                              />
                            ))}
                          </fieldset>
                        ))}
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}
      </Card.Body>
    </Card>
  );
};

export default ConfigurationsTab;
